import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import CertificateSettingsDialogFields from "../../field/settings/CertificateSettingsDialogFields";
import AbstractDialog, {
	AbstractDialogProperties,
	AbstractDialogState
} from "../../../../shared/dialog/components/AbstractDialog";
import {
	CertificateEntry,
	KeyStoreEntry,
	KeyStorePassword,
	KeyStorePasswordInterface,
	Origin,
	UserCertificates
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import UserSettings from "../../../session/UserSettings";
import {Accordion, AccordionTab} from "primereact/accordion";
import {Password} from "primereact/password";
import Exception from "../../../../shared/exception/Exception";
import CertificateDialog from "../security/CertificateDialog";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import PortalContext from "../../../PortalContext";
import PortalDialogType from "../../PortalDialogType";
import Parameter from "../../parameter/Parameter";
import DialogDefault from "../../parameter/PortalDialogDefault";
import {InputSwitch} from "primereact/inputswitch";
import Icon from "../../../../shared/components/Icon";
import Immutable from "../../../../shared/immutable/Immutable";
import SortHelper from "../../../../shared/helper/SortHelper";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export interface CertificateSettingsDialogProperties extends AbstractDialogProperties, I18nGlobals {
	context: PortalContext;
	userCertificates: Immutable<UserCertificates>;
	userSettings: Immutable<UserSettings>;
}

interface CertificateSettingsDialogState extends AbstractDialogState {
	userSettings?: Immutable<UserSettings>;
	showCertificateDialog: boolean;
	selectedCertificates: Array<CertificateEntry>;
	keyStorePasswords: Map<string, KeyStorePassword>;
}

export class CertificateSettingsDialogDefinition extends AbstractDialog<CertificateSettingsDialogProperties,
	CertificateSettingsDialogState> {
	constructor(props: Readonly<CertificateSettingsDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			showCertificateDialog: false,
			selectedCertificates: [],
			keyStorePasswords: new Map<string, KeyStorePassword>()
		}

		this.getParameter = this.getParameter.bind(this);
	}

	private getParameter(keyStoreName: string): KeyStorePassword {
		return FunctionHelper.applyValue(
			this.state.keyStorePasswords.get(keyStoreName), this.getDefaultParameter().data
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["certificate-settings-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<>
				<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
						header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
						appendTo={document.getElementById("root")}>
					<div className="p-grid">
						{this.renderError()}
						<Helper {...this.props}>
							<Translate {...this.props} data={"dialog.settings.certificate.general"} namespace={"help"}
									   markdown={true}/>
						</Helper>
						{this.renderContents()}
					</div>
				</Dialog>
				<CertificateDialog {...this.props} visible={this.state.showCertificateDialog}
								   certificates={this.state.selectedCertificates}
								   hide={() => this.setState({
									   showCertificateDialog: false
								   })}/>
			</>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.close")} icon="icon icon-close"
							onClick={() => this.hide()} className="dialog-button"/>
				</div>
			</div>
		);
	}

	private renderCertificate(keystore: KeyStoreEntry, certificate: CertificateEntry, key: number): ReactNode {
		let parameter: KeyStorePassword = this.getParameter(keystore.keyStoreName!);
		let parsedCertificate: x509.X509Certificate | undefined;
		let isGlobal: boolean = keystore.keyStoreOrigin === Origin.Global;

		try {
			parsedCertificate = new x509.X509Certificate(certificate.certificate);
		} catch (e: any) {
			return null;
		}

		return (
			<div className="p-col-12 p-grid" key={key}>
				<p className="certificate-subject p-col-10">
					<Button icon="icon icon-info"
							onClick={(e) => {
								this.setState({
									showCertificateDialog: true,
									selectedCertificates: [certificate]
								});
							}}/>
					{certificate.hasPrivateKey &&
						<Icon icon={FunctionHelper.applyValue(
							certificate.isPrivateKeyReadable, CertificateEntry.getIsPrivateKeyReadableDefault()
						) ? "icon-unlocked" : "icon-locked"}
							  tooltip={
								  <Translate {...this.props} data={FunctionHelper.applyValue(
									  certificate.isPrivateKeyReadable,
									  CertificateEntry.getIsPrivateKeyReadableDefault()
								  ) ? "settings.certificate.unlocked" : "settings.certificate.locked"
								  } namespace="dialog"/>
							  }/>
					}
					{certificate.hasPrivateKey &&
						<Icon icon={"icon-key"}
							  tooltip={
								  <Translate {...this.props} data={"settings.certificate.key"} namespace="dialog"/>
							  }/>
					}
					{parsedCertificate.subject}
				</p>
				<div className="p-col-2 p-grid">
					{certificate.isPrivateKeyReadable && certificate.hasPrivateKey && !isGlobal &&
						<Button icon="icon icon-locked small" tooltip={Translate.value("buttons.lock")}
								onClick={() => this.lockCertificate(keystore.keyStoreName!, certificate.aliasName!)}/>
					}
				</div>
				{!certificate.isPrivateKeyReadable && certificate.hasPrivateKey && !isGlobal &&
					<>
						<div className="p-col-10 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor={"control-certificate-password-" + key}>
								<Translate {...this.props} data="settings.certificate.password" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<Password id={"control-certificate-password-" + key} feedback={false}
										  toggleMask={true} appendTo={document.getElementById("root")}
										  value={FunctionHelper.applyValue(
											  parameter.aliases![certificate.aliasName!],
											  KeyStorePassword.getKeyStorePasswordDefault()
										  )}
										  onChange={(e) => this.onChange(
											  CertificateSettingsDialogFields.CERTIFICATE_PASSWORD,
											  keystore.keyStoreName!,
											  {[certificate.aliasName!]: e.currentTarget.value}
										  )}/>
							</div>
						</div>
						<div className="p-col-2 p-grid">
							<Button disabled={!this.isValid()} tooltip={Translate.value("buttons.unlock")}
									icon={
										"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-unlocked small")
									}
									onClick={() => this.unlockCertificate(
										keystore.keyStoreName!, certificate.aliasName
									)}/>
						</div>
					</>
				}
			</div>
		)
	}

	private renderKeystore(keystore: KeyStoreEntry, certificates: Array<CertificateEntry>, key: number): ReactNode {
		let parameter: KeyStorePassword = this.getParameter(keystore.keyStoreName!);

		let hasLockedCertificates: boolean = false;
		let certificateNode: Array<ReactNode> = [];
		if (keystore.isKeyStoreAccessible) {
			for (let certificate of certificates) {
				let isGlobal: boolean = certificate.keyStoreOrigin === Origin.Global;
				if ((certificate.keyStoreName !== keystore.keyStoreName && !isGlobal) ||
					keystore.keyStoreOrigin !== certificate.keyStoreOrigin) {
					continue;
				}

				certificateNode.push(this.renderCertificate(keystore, certificate, certificateNode.length));

				if (!certificate.isPrivateKeyReadable && certificate.hasPrivateKey && !isGlobal) {
					hasLockedCertificates = true;
				}
			}
		}

		let isGlobalKeystore: boolean = keystore.keyStoreOrigin === Origin.Global;

		return (
			<AccordionTab key={key} headerTemplate={
				<>
					<Icon icon={isGlobalKeystore ? "icon-globe small" : "icon-user"}
						  tooltip={
							  <Translate {...this.props} data={
								  isGlobalKeystore ?
									  "settings.certificate.keystore.global" : "settings.certificate.keystore.user"
							  } namespace="dialog"/>
						  }/>{keystore.keyStoreName}
				</>
			}>
				{keystore.isKeyStoreAccessible ?
					<>
						{certificateNode}
						{hasLockedCertificates &&
							<div className="p-col-12 p-grid">
								<label className="p-col-12 p-sm-4"></label>
								<div className="p-col-12 p-sm-8 p-grid">
									<Button label={Translate.value("buttons.unlock")} disabled={!this.isValid()}
											icon={"icon" + (
												this.state.submitted ? " icon-spinner spin" : " icon-unlocked small"
											)}
											onClick={() => this.unlockCertificate(keystore.keyStoreName!)}/>
								</div>
							</div>
						}
					</> :
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor={"control-keystore-password-" + key}>
								<Translate {...this.props} data="settings.certificate.keystore.password"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<Password id={"control-keystore-password-" + key} feedback={false} toggleMask={true}
										  appendTo={document.getElementById("root")}
										  value={FunctionHelper.applyValue(
											  parameter.keyStorePassword,
											  KeyStorePassword.getKeyStorePasswordDefault()
										  )}
										  onChange={(e) => this.onChange(
											  CertificateSettingsDialogFields.KEYSTORE_PASSWORD, keystore.keyStoreName!,
											  e.currentTarget.value
										  )}/>
							</div>
						</div>
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-4"></label>
							<div className="p-col-12 p-sm-8 p-grid">
								<Button label={Translate.value("buttons.unlock")} disabled={!this.isValid()}
										icon={"icon" + (
											this.state.submitted ? " icon-spinner spin" : " icon-unlocked small"
										)}
										onClick={() => this.unlockKeystore(keystore.keyStoreName!)}/>
							</div>
						</div>
					</fieldset>
				}
			</AccordionTab>
		)
	}

	public renderContents(): ReactNode {
		let certificates: UserCertificates = UserCertificates.fromJson(this.props.userCertificates.get());

		let keystoreNode: Array<ReactNode> = [];
		let sortedKeystores: Array<KeyStoreEntry> = certificates.keyStores.sort(
			(a: KeyStoreEntry, b: KeyStoreEntry) => {
				return SortHelper.alphanum(
					FunctionHelper.applyValue(a.keyStoreName, ""),
					FunctionHelper.applyValue(b.keyStoreName, "")
				);
			});

		for (let keystore of sortedKeystores) {
			keystoreNode.push(this.renderKeystore(keystore, certificates.certificates, keystoreNode.length));
		}

		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid">
						<div className="p-col-12 p-sm-12 switch-container">
							<label className="switch-label" htmlFor="control-hints" style={{marginRight: "10px"}}>
								<Translate {...this.props} data="settings.certificate.hints" namespace="dialog"/>
							</label>
							<InputSwitch inputId="control-hints" checked={userSettings.disableCertificateHints}
										 onChange={(e) => {
											 userSettings.disableCertificateHints = e.value || false;
											 this.props.context.session.updateUserSettings(userSettings);
										 }}/>
						</div>
					</div>
					<div className="certificates p-col-12 p-grid" style={{marginTop: "15px"}}>
						<Accordion activeIndex={0} className="p-col-12">
							{keystoreNode}
						</Accordion>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: CertificateSettingsDialogFields, keyStoreName: string, value: any): void {
		let keyStorePasswords: Map<string, KeyStorePassword> = this.state.keyStorePasswords;
		let parameter: KeyStorePassword = this.getParameter(keyStoreName);

		switch (field) {
			case CertificateSettingsDialogFields.KEYSTORE_PASSWORD:
				parameter.keyStorePassword = value;
				break;
			case CertificateSettingsDialogFields.CERTIFICATE_PASSWORD:
				for (let [key, password] of Object.entries(value)) {
					parameter.aliases![key] = password as string;
				}
				break;
		}

		keyStorePasswords.set(keyStoreName, parameter);
		this.setState({
			keyStorePasswords: keyStorePasswords
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-certificate small"}/>
					<Translate {...this.props} data="settings.certificate.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());

		this.setState({
			userSettings: Immutable.from(userSettings.toJson())
		});
	};

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted;
	}

	protected async unlockKeystore(keyStoreName: string): Promise<void> {
		await super.submit();

		let parameter: KeyStorePassword = this.getParameter(keyStoreName);
		parameter = KeyStorePassword.fromJson({
			keyStorePassword: parameter.keyStorePassword
		} as KeyStorePasswordInterface);
		let certificates: UserCertificates | undefined;

		try {
			certificates = await this.props.context.session.updateCertificates(keyStoreName, parameter);
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.unlockKeystore(keyStoreName);
			}
			return;
		}

		let exception: Exception | undefined;
		for (let keystore of FunctionHelper.applyValue(
			certificates?.keyStores, UserCertificates.getKeyStoresDefault()
		)) {
			if (keystore.keyStoreName === keyStoreName && !keystore.isKeyStoreAccessible) {
				exception = new Exception(
					Translate.value("errors.settings.certificate.keystore.unlock", {
						ns: "message", keystore: keyStoreName
					})
				);
				break;
			}
		}

		this.setState({
			submitted: false,
			exception: exception
		}, () => {
			let userSettings: UserSettings = UserSettings.fromJson(this.state.userSettings!.get());
			this.props.context.session.updateUserSettings(userSettings);
		});
	}

	protected async lockCertificate(keyStoreName: string, alias: string): Promise<void> {
		await super.submit();

		let parameter: KeyStorePassword = KeyStorePassword.fromJson({
			aliases: {
				[alias]: null
			}
		});
		let certificates: UserCertificates | undefined = UserCertificates.fromJson(this.props.userCertificates.get());

		try {
			certificates = await this.props.context.session.updateCertificates(keyStoreName, parameter);
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.unlockCertificate(keyStoreName, alias);
			}
			return;
		}

		let exception: Exception | undefined;
		for (let certificate of FunctionHelper.applyValue(
			certificates?.certificates, UserCertificates.getCertificatesDefault()
		)) {
			if (certificate.aliasName !== alias) {
				continue;
			}

			if (certificate.isPrivateKeyReadable) {
				let name: string;

				try {
					let parsedCertificate: x509.X509Certificate = new x509.X509Certificate(certificate.certificate);
					name = parsedCertificate.subject;
				} catch (e: any) {
					name = certificate.aliasName;
				}

				exception = new Exception(
					Translate.value("errors.settings.certificate.alias.lock", {
						ns: "message", name: name
					})
				);
			}

			break;
		}

		this.setState({
			submitted: false,
			exception: exception
		});
	}

	protected async unlockCertificate(keyStoreName: string, alias?: string): Promise<void> {
		await super.submit();

		let parameter: KeyStorePassword = this.getParameter(keyStoreName);
		let certificates: UserCertificates | undefined = UserCertificates.fromJson(this.props.userCertificates.get());

		if (FunctionHelper.exists(alias)) {
			parameter = KeyStorePassword.fromJson({
				aliases: {
					[alias!]: FunctionHelper.applyValue(parameter.aliases![alias!], "")
				}
			} as KeyStorePasswordInterface);
		} else {
			for (let certificate of FunctionHelper.applyValue(
				certificates?.certificates, UserCertificates.getCertificatesDefault()
			)) {
				if (!certificate.hasPrivateKey || certificate.keyStoreName !== keyStoreName ||
					certificate.isPrivateKeyReadable) {
					continue;
				}

				parameter.aliases![certificate.aliasName!] = FunctionHelper.applyValue(
					parameter.aliases![certificate.aliasName!], ""
				)
			}
		}

		try {
			certificates = await this.props.context.session.updateCertificates(keyStoreName, parameter);
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.unlockCertificate(keyStoreName, alias);
			}
			return;
		}

		let exception: Exception | undefined;
		if (FunctionHelper.exists(alias)) {
			for (let certificate of FunctionHelper.applyValue(
				certificates?.certificates, UserCertificates.getCertificatesDefault()
			)) {
				if (certificate.aliasName !== alias) {
					continue;
				}

				if (!certificate.isPrivateKeyReadable) {
					let name: string;

					try {
						let parsedCertificate: x509.X509Certificate = new x509.X509Certificate(certificate.certificate);
						name = parsedCertificate.subject;
					} catch (e: any) {
						name = certificate.aliasName;
					}

					exception = new Exception(
						Translate.value("errors.settings.certificate.alias.unlock", {
							ns: "message", name: name
						})
					);
				}

				break;
			}
		} else {
			for (let certificate of FunctionHelper.applyValue(
				certificates?.certificates, UserCertificates.getCertificatesDefault()
			)) {
				if (certificate.hasPrivateKey && !certificate.isPrivateKeyReadable &&
					certificate.keyStoreName === keyStoreName) {
					let name: string;

					try {
						let parsedCertificate: x509.X509Certificate = new x509.X509Certificate(certificate.certificate);
						name = parsedCertificate.subject;
					} catch (e: any) {
						name = FunctionHelper.applyValue(certificate.aliasName, "");
					}

					exception = new Exception(
						Translate.value("errors.settings.certificate.alias.unlock", {
							ns: "message", name: name
						})
					);
					break;
				}
			}
		}

		this.setState({
			submitted: false,
			exception: exception
		});
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.SETTINGS_CERTIFICATE;
	}

	protected getDefaultParameter(): Parameter<KeyStorePassword> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const CertificateSettingsDialog = React.memo(CertificateSettingsDialogDefinition,
	(
		prevProps: Readonly<CertificateSettingsDialogProperties>,
		nextProps: Readonly<CertificateSettingsDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default CertificateSettingsDialog;